import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import InvoiceChart, { InvoicePeriodRow, PeriodGrain, rollUpPeriods } from './invoice-chart';

export type ClientInvoiceStats = { count: number; totalAmount: number; periods: InvoicePeriodRow[] };

// The last 12 complete months as "YYYY-MM" bounds: the month 12 months back
// through last month - the current month and anything dated later are left
// out (same window as invoiceTrend). asOf: what "now" is - a Xero company's
// last invoice sync (its invoices are only that current), else today.
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const last12Window = (asOf?: Date | null) => {
    const now = asOf ?? new Date();
    return {
        from: monthKey(new Date(now.getFullYear(), now.getMonth() - 12, 1)),
        to: monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    };
};

// A client's invoices in the last 12 months and since the beginning.
export const summarizeClientInvoices = (stats?: ClientInvoiceStats, asOf?: Date | null) => {
    const { from, to } = last12Window(asOf);
    const recent = (stats?.periods ?? []).filter(p => p.period >= from && p.period <= to);
    return {
        last12Amount: recent.reduce((s, p) => s + p.totalAmount, 0),
        last12Count: recent.reduce((s, p) => s + p.count, 0),
        allAmount: stats?.totalAmount ?? 0,
        allCount: stats?.count ?? 0,
    };
};

// Whether a client's monthly invoiced dollars are rising or falling over the
// last 12 complete months (up to last month - the current month and anything
// dated later are left out): the slope of a least-squares line through the 12 monthly
// totals (months with no invoices count as 0). A change over the year of
// under 5% of the average month counts as flat.
export type InvoiceTrend = { direction: 'up' | 'down' | 'flat'; slope: number };
export const invoiceTrend = (stats?: ClientInvoiceStats, asOf?: Date | null): InvoiceTrend => {
    const byPeriod = new Map((stats?.periods ?? []).map(p => [p.period, p.totalAmount]));
    const d = new Date((asOf ?? new Date()).getTime());
    d.setDate(1);
    d.setMonth(d.getMonth() - 12);
    const ys: number[] = [];
    for (let i = 0; i < 12; i++) {
        ys.push(byPeriod.get(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`) ?? 0);
        d.setMonth(d.getMonth() + 1);
    }
    const n = ys.length;
    const sumX = (n * (n - 1)) / 2;
    const sumX2 = ((n - 1) * n * (2 * n - 1)) / 6;
    const sumY = ys.reduce((s, y) => s + y, 0);
    const sumXY = ys.reduce((s, y, x) => s + x * y, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const mean = sumY / n;
    if (!mean || Math.abs(slope * (n - 1)) < mean * 0.05) return { direction: 'flat', slope };
    return { direction: slope > 0 ? 'up' : 'down', slope };
};

// Several clients' stats as one - the Clients page's Total row.
export const combineClientStats = (list: (ClientInvoiceStats | undefined)[]): ClientInvoiceStats => {
    const byPeriod = new Map<string, InvoicePeriodRow>();
    let count = 0;
    let totalAmount = 0;
    for (const stats of list) {
        if (!stats) continue;
        count += stats.count;
        totalAmount += stats.totalAmount;
        for (const p of stats.periods) {
            const row = byPeriod.get(p.period) || { period: p.period, count: 0, totalAmount: 0 };
            row.count += p.count;
            row.totalAmount += p.totalAmount;
            byPeriod.set(p.period, row);
        }
    }
    return { count, totalAmount, periods: Array.from(byPeriod.values()).sort((a, b) => a.period.localeCompare(b.period)) };
};

type Props = {
    stats?: ClientInvoiceStats;
    isLoading?: boolean;
    formatAmount: (n: number) => string;
};

// A Clients page row's expanded panel: the client's invoice count and total,
// and both over time (per month/quarter/year of issue date) - always at
// least the last 12 months.
const ClientInvoicePanel: React.FC<Props> = ({ stats, isLoading, formatAmount }) => {
    const [grain, setGrain] = React.useState<PeriodGrain>('month');
    const rows = React.useMemo(() => rollUpPeriods(stats?.periods ?? [], grain, 12), [stats, grain]);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-container-background)', borderRadius: 1, px: { xs: 1, sm: 2 }, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {stats
                        ? `${stats.count} invoice${stats.count === 1 ? '' : 's'} · ${formatAmount(stats.totalAmount)} total`
                        : isLoading ? 'Loading invoices...' : 'No invoices yet'}
                </Typography>
                {stats && (
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={grain}
                        onChange={(_, v) => v && setGrain(v)}
                        sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none' } }}
                    >
                        <ToggleButton value="month">Month</ToggleButton>
                        <ToggleButton value="quarter">Quarter</ToggleButton>
                        <ToggleButton value="year">Year</ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>
            {stats && <InvoiceChart rows={rows} labelHeader="Period" formatAmount={formatAmount} />}
        </Box>
    );
};

export default ClientInvoicePanel;
